import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { API_BASE, getAuthHeaders } from "../../../helpers/apiConfig";

const BASE_URL = API_BASE;

const SertifikaTanimlari = () => {
  document.title = "Sertifika Tanımları | HarmanaGEL";

  const [sertifikalar, setSertifikalar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ ID: null, AD: "", ACIKLAMA: "", AKTIF: true });

  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setFormData({ ID: null, AD: "", ACIKLAMA: "", AKTIF: true });
      setIsEditing(false);
    }
  };

  const fetchSertifikalar = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BASE_URL}/SERTIFIKA_TANIMLARI?$orderby=ID asc`, { headers: getAuthHeaders() });
      const result = await response.json();
      if (result && result.value) setSertifikalar(result.value);
    } catch (err) {
      setError("Sertifika tanımları yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSertifikalar();
  }, []);

  const handleEdit = (sert) => {
    setFormData({
      ID: sert.ID,
      AD: sert.AD || sert.SERTIFIKA_ADI || "",
      ACIKLAMA: sert.ACIKLAMA || "",
      AKTIF: sert.AKTIF !== false,
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.AD.trim()) {
      setError("Sertifika adı zorunludur.");
      return;
    }
    try {
      const payload = {
        AD: formData.AD,
        ACIKLAMA: formData.ACIKLAMA,
        AKTIF: formData.AKTIF,
      };
      if (isEditing) {
        await fetch(`${BASE_URL}/SERTIFIKA_TANIMLARI(${formData.ID})`, {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });
        setSuccessMsg("Sertifika tanımı güncellendi.");
      } else {
        await fetch(`${BASE_URL}/SERTIFIKA_TANIMLARI`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });
        setSuccessMsg("Sertifika tanımı eklendi.");
      }
      toggleModal();
      fetchSertifikalar();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Kayıt sırasında hata oluştu.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu sertifika tanımını silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/SERTIFIKA_TANIMLARI(${id})`, { method: "DELETE", headers: getAuthHeaders() });
      setSuccessMsg("Sertifika tanımı silindi.");
      fetchSertifikalar();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Sertifika Adı",
      accessorKey: "AD",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => {
        const val = cell.getValue() || cell.row.original.SERTIFIKA_ADI || "-";
        return <span className="fw-medium">{val}</span>;
      },
    },
    {
      header: "Açıklama",
      accessorKey: "ACIKLAMA",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => cell.getValue() || <span className="text-muted">-</span>,
    },
    {
      header: "Durum",
      accessorKey: "AKTIF",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color={cell.getValue() !== false ? "success" : "secondary"}>
          {cell.getValue() !== false ? "Aktif" : "Pasif"}
        </Badge>
      ),
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const s = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(s)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(s.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Sertifika Tanımları" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Sertifika Tanımları</h4>
                    <Button color="primary" size="sm" onClick={() => { setIsEditing(false); setModal(true); }}>
                      <i className="bx bx-plus me-1" />
                      Yeni Sertifika Tanımı
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={sertifikalar}
                      isGlobalFilter={true}
                      SearchPlaceholder="Sertifika ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>
          {isEditing ? "Sertifika Düzenle" : "Yeni Sertifika Ekle"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Sertifika Adı *</Label>
            <Input
              value={formData.AD}
              onChange={(e) => setFormData({ ...formData, AD: e.target.value })}
              placeholder="Sertifika adını girin"
            />
          </FormGroup>
          <FormGroup>
            <Label>Açıklama</Label>
            <Input
              type="textarea"
              rows={3}
              value={formData.ACIKLAMA}
              onChange={(e) => setFormData({ ...formData, ACIKLAMA: e.target.value })}
              placeholder="Sertifika açıklaması"
            />
          </FormGroup>
          <FormGroup check>
            <Input
              type="checkbox"
              checked={formData.AKTIF}
              onChange={(e) => setFormData({ ...formData, AKTIF: e.target.checked })}
            />
            <Label check>Aktif</Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={toggleModal}>İptal</Button>
          <Button color="primary" onClick={handleSave}>
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SertifikaTanimlari;
